'use client'

import * as React from 'react'
import { format, isValid, parse } from 'date-fns'
import { Calendar } from '@/components/ui/Calendar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/Popover'
import { Input } from './Input'
import { CalendarDaysIcon } from '@heroicons/react/16/solid'

export type DatePickerProps = React.ComponentProps<typeof Calendar>

type EnhancedDatePickerProps = DatePickerProps &
  React.ComponentProps<typeof Input>

export default function DatePicker({
  value: value,
  onSelect: setSelectedDate,
  selected: selectedDate,
  placeholder,
  status,
  inputDisabled,
  ...props
}: EnhancedDatePickerProps) {
  const inputRef = React.useRef(null)

  const [inputValue, setInputValue] = React.useState(
    format(value, 'dd/MM/yyyy'),
  )
  const [month, setMonth] = React.useState<Date>(selectedDate ?? new Date())

  const handleDayPickerSelect = (value: Date | undefined) => {
    setSelectedDate(format(value, 'yyyy-MM-dd'))
    inputRef.current?.click()
    inputRef.current?.focus()
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value)

    const parsedDate = parse(e.target.value, 'dd/MM/yyyy', new Date())

    if (isValid(parsedDate)) {
      setSelectedDate(parsedDate)
      setMonth(parsedDate)
    } else {
      setSelectedDate(undefined)
    }
  }

  React.useEffect(() => {
    setInputValue(format(value, 'dd MMMM yyyy'))
    if (value && isValid(new Date(value))) {
      setMonth(new Date(value))
    }
  }, [value])

  return (
    <Popover modal>
      <PopoverTrigger className="w-full" data-slot="control">
        <Input
          disabled={inputDisabled}
          value={inputValue || format(new Date(), 'dd MMMM yyyy')}
          onChange={handleInputChange}
          ref={inputRef}
          placeholder={placeholder}
          leadingIcon={<CalendarDaysIcon />}
          status={status}
        />
      </PopoverTrigger>
      {!inputDisabled && (
        <PopoverContent className="w-auto p-0">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={handleDayPickerSelect}
            month={month}
            onMonthChange={setMonth}
            initialFocus
            footer={
              <div className="mt-2 border-t pt-2">
                <button
                  type="button"
                  className="w-full rounded px-2 py-1 text-center text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                  onClick={() => setMonth(new Date())}
                >
                  Today
                </button>
              </div>
            }
            {...props}
          />
        </PopoverContent>
      )}
    </Popover>
  )
}
